import { createStore, createHook, createActionsHook } from 'react-sweet-state';
import type { Action, StoreActionApi } from 'react-sweet-state';

import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';
import type { useUniversalPreset } from '@atlaskit/editor-core/preset-universal';

export type MentionType = {
	type: 'deleted' | 'added';
	localId: string;
	id: string;
	displayName?: string;
	taskLocalId?: string;
	userPhoto?: string;
	method?: 'typed' | 'pasted';
};

type PresetAPIType = ExtractPresetAPI<ReturnType<typeof useUniversalPreset>>;

type MentionsNotificationsState = {
	deletedMentions: MentionType[];
	addedMentions: MentionType[];
	deletedTasks: MentionType[];
	addedTasks: MentionType[];
	unrestrictedMentions: MentionType[];
	parentSpaceRestrictedMentions: MentionType[];
	pageRestrictedMentions: MentionType[];
	unrestrictedTasks: MentionType[];
	parentSpaceRestrictedTasks: MentionType[];
	pageRestrictedTasks: MentionType[];
	editorApi?: PresetAPIType;
	mentionsAdded: {
		currentUser: MentionType[];
		otherUsers: MentionType[];
	};
	tasksAdded: {
		currentUser: MentionType[];
		otherUsers: MentionType[];
	};
};

export type HandleMentionsChangedProps = {
	mentionChanges: MentionType[];
	deletedMentions: MentionType[];
	addedMentions: MentionType[];
	setDeletedMentions: (mentions: MentionType[]) => void | Promise<void>;
	setAddedMentions: (mentions: MentionType[]) => void | Promise<void>;
	userId: string | null;
	unrestrictedMentions: MentionType[];
	parentSpaceRestrictedMentions: MentionType[];
	pageRestrictedMentions: MentionType[];
	unrestrictedTasks: MentionType[];
	addedTasks: MentionType[];
	deletedTasks: MentionType[];
	setAddedTasks: (mentions: MentionType[]) => void | Promise<void>;
	setDeletedTasks: (mentions: MentionType[]) => void | Promise<void>;
	pageRestrictedTasks: MentionType[];
	parentSpaceRestrictedTasks: MentionType[];
	setMentionsAdded: (mentionsToAdd: { currentUser?: MentionType[] }) => void;
	setTasksAdded: (tasksToAdd: { currentUser?: MentionType[] }) => void;
};

const getInitialState = (): MentionsNotificationsState => ({
	deletedMentions: [],
	addedMentions: [],
	deletedTasks: [],
	addedTasks: [],
	unrestrictedMentions: [], //Added mentions which have access to the current page
	parentSpaceRestrictedMentions: [], //Added mentions which dont have access to the current page due to parent or space restrictions
	pageRestrictedMentions: [], //Added mentions which dont have access to the current page due to page restrictions
	unrestrictedTasks: [], //Added task mentions which have access to the current page
	parentSpaceRestrictedTasks: [], //Added task mentions which dont have access to the current page due to parent or space restrictions
	pageRestrictedTasks: [], //Added task mentions which dont have access to the current page due to page restrictions
	editorApi: undefined,
	mentionsAdded: {
		currentUser: [], //Mentions of current user added by the current user
		otherUsers: [], //Mentions of other users added by the current user
	},
	tasksAdded: {
		currentUser: [], //Task mentions of current user added by the current user
		otherUsers: [], //Task mentions of other users added by the current user
	},
});

const actions = {
	setDeletedMentions:
		(mentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				deletedMentions: mentions,
			});
		},
	setAddedMentions:
		(mentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				addedMentions: mentions,
			});
		},
	setUnrestrictedMentions:
		(mentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				unrestrictedMentions: mentions,
			});
		},
	addUnrestrictedMentions:
		(addedUnrestrictedMentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { unrestrictedMentions } = getState();
			const updatedUnrestrictedMentions = [...unrestrictedMentions, ...addedUnrestrictedMentions];
			setState({
				unrestrictedMentions: updatedUnrestrictedMentions,
			});
		},
	removeUnrestrictedMentions:
		(removedUnrestrictedMention: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(removedUnrestrictedMention.map((mention) => mention.localId));
			const { unrestrictedMentions } = getState();
			const updatedUnrestrictedMentions = unrestrictedMentions.filter(
				(mention) => !removedLocalIds.has(mention.localId),
			);
			setState({
				unrestrictedMentions: updatedUnrestrictedMentions,
			});
		},
	setParentSpaceRestrictedMentions:
		(mentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				parentSpaceRestrictedMentions: mentions,
			});
		},
	addParentSpaceRestrictedMentions:
		(addedParentSpaceRestrictedMentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { parentSpaceRestrictedMentions } = getState();
			const updatedParentSpaceRestrictedMentions = [
				...parentSpaceRestrictedMentions,
				...addedParentSpaceRestrictedMentions,
			];
			setState({
				parentSpaceRestrictedMentions: updatedParentSpaceRestrictedMentions,
			});
		},
	removeParentSpaceRestrictedMentions:
		(removedParentSpaceRestrictedMentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(
				removedParentSpaceRestrictedMentions.map((mention) => mention.localId),
			);
			const { parentSpaceRestrictedMentions } = getState();
			const updatedParentSpaceRestrictedMentions = parentSpaceRestrictedMentions.filter(
				(mention) => !removedLocalIds.has(mention.localId),
			);
			setState({
				parentSpaceRestrictedMentions: updatedParentSpaceRestrictedMentions,
			});
		},
	setPageRestrictedMentions:
		(mentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				pageRestrictedMentions: mentions,
			});
		},
	addPageRestrictedMentions:
		(addedPageRestrictedMentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { pageRestrictedMentions } = getState();
			const updatedPageRestrictedMention = [
				...pageRestrictedMentions,
				...addedPageRestrictedMentions,
			];
			setState({
				pageRestrictedMentions: updatedPageRestrictedMention,
			});
		},
	removePageRestrictedMentions:
		(removedPageRestrictedMentions: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(
				removedPageRestrictedMentions.map((mention) => mention.localId),
			);
			const { pageRestrictedMentions } = getState();
			const updatedPageRestrictedMention = pageRestrictedMentions.filter(
				(mention) => !removedLocalIds.has(mention.localId),
			);
			setState({
				pageRestrictedMentions: updatedPageRestrictedMention,
			});
		},
	setDeletedTasks:
		(tasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				deletedTasks: tasks,
			});
		},
	setAddedTasks:
		(tasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				addedTasks: tasks,
			});
		},
	setPageRestrictedTasks:
		(tasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				pageRestrictedTasks: tasks,
			});
		},
	addPageRestrictedTasks:
		(addedPageRestrictedTasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { pageRestrictedTasks } = getState();
			const updatedPageRestrictedTasks = [...pageRestrictedTasks, ...addedPageRestrictedTasks];
			setState({
				pageRestrictedTasks: updatedPageRestrictedTasks,
			});
		},
	removePageRestrictedTasks:
		(removedPageRestrictedTasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(removedPageRestrictedTasks.map((tasks) => tasks.localId));
			const { pageRestrictedTasks } = getState();
			const updatedPageRestrictedTasks = pageRestrictedTasks.filter(
				(tasks) => !removedLocalIds.has(tasks.localId),
			);
			setState({
				pageRestrictedTasks: updatedPageRestrictedTasks,
			});
		},
	setParentSpaceRestrictedTasks:
		(tasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				parentSpaceRestrictedTasks: tasks,
			});
		},
	addParentSpaceRestrictedTasks:
		(addedParentSpaceRestrictedTasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { parentSpaceRestrictedTasks } = getState();
			const updatedParentSpaceRestrictedTasks = [
				...parentSpaceRestrictedTasks,
				...addedParentSpaceRestrictedTasks,
			];
			setState({
				parentSpaceRestrictedTasks: updatedParentSpaceRestrictedTasks,
			});
		},
	removeParentSpaceRestrictedTasks:
		(removedParentSpaceRestrictedTasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(
				removedParentSpaceRestrictedTasks.map((tasks) => tasks.localId),
			);
			const { parentSpaceRestrictedTasks } = getState();
			const updatedParentSpaceRestrictedTasks = parentSpaceRestrictedTasks.filter(
				(tasks) => !removedLocalIds.has(tasks.localId),
			);
			setState({
				parentSpaceRestrictedTasks: updatedParentSpaceRestrictedTasks,
			});
		},
	setUnrestrictedTasks:
		(tasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState }) => {
			setState({
				unrestrictedTasks: tasks,
			});
		},
	addUnrestrictedTasks:
		(addedUnrestrictedTasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { unrestrictedTasks } = getState();
			const updatedUnrestrictedTasks = [...unrestrictedTasks, ...addedUnrestrictedTasks];
			setState({
				unrestrictedTasks: updatedUnrestrictedTasks,
			});
		},
	removeUnrestrictedTasks:
		(removedUnrestrictedTasks: MentionType[]): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const removedLocalIds = new Set(removedUnrestrictedTasks.map((tasks) => tasks.localId));
			const { unrestrictedTasks } = getState();
			const updatedUnrestrictedTasks = unrestrictedTasks.filter(
				(tasks) => !removedLocalIds.has(tasks.localId),
			);
			setState({
				unrestrictedTasks: updatedUnrestrictedTasks,
			});
		},
	setMentionsAdded:
		(mentionsToAdd: { currentUser?: MentionType[] }): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { mentionsAdded } = getState();
			const updatedMentionsAdded = mentionsAdded;
			if (mentionsToAdd.currentUser) {
				if (mentionsToAdd.currentUser.length > 0) {
					const currentUserMentions = mentionsToAdd.currentUser;
					updatedMentionsAdded.currentUser = [...currentUserMentions, ...mentionsAdded.currentUser];
				} else {
					updatedMentionsAdded.currentUser = [];
				}
			}
			setState({ mentionsAdded: updatedMentionsAdded });
		},
	setTasksAdded:
		(tasksToAdd: { currentUser?: MentionType[] }): Action<MentionsNotificationsState> =>
		({ setState, getState }) => {
			const { tasksAdded } = getState();
			const updatedTasksAdded = tasksAdded;
			if (tasksToAdd.currentUser) {
				if (tasksToAdd.currentUser.length > 0) {
					const currentUserTasks = tasksToAdd.currentUser;
					updatedTasksAdded.currentUser = [...currentUserTasks, ...tasksAdded.currentUser];
				} else {
					updatedTasksAdded.currentUser = [];
				}
			}
			setState({ tasksAdded: updatedTasksAdded });
		},
	clearMentionsFlags:
		() =>
		({ setState }: StoreActionApi<MentionsNotificationsState>) => {
			setState(getInitialState());
		},
};

const MentionsNotificationsStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'MentionsNotificationsStore',
});

export const useMentionsNotifications = createHook(MentionsNotificationsStore);
export const useMentionsNotificationsActions = createActionsHook(MentionsNotificationsStore);
